/**
 * @fileoverview Deterministic lesson relevance scoring engine.
 *
 * Scores previously recorded bugfix lessons against a current bug input
 * using multi-dimensional signal matching: app identity, path overlap,
 * control/symptom compatibility, component chain similarity, and
 * weighted token overlap (expected-outcome tokens get 4× weight).
 *
 * Used by both the browser-side chat widget (via
 * `ai-information.service.ts`) and optionally by CI runners for
 * lesson-driven fix planning.
 *
 * @packageDocumentation
 */

import type { AiBugfixLessonRecord, AiRelevantBugfixLesson, AiLessonTargetElementHint, LessonScoringWeights } from './types';
import { LESSON_SCORING_WEIGHTS } from './weights.config';

/**
 * Tokenise text for lesson matching: camelCase split → punctuation removal → lowercase → whitespace split.
 */
export function tokenizeAiBugfixLessonText(value: unknown): string[] {
  return String(value || '')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[^A-Za-z0-9]+/g, ' ')
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean);
}

const GENERIC_TOKENS = new Set([
  'app', 'bug', 'context', 'current', 'element', 'expected',
  'hff', 'home', 'html', 'input', 'page', 'selected',
  'selector', 'should', 'ssr', 'the', 'this', 'with'
]);

/**
 * Extract signal tokens from lesson-related text, filtering out
 * generic stopwords and tokens shorter than 4 characters.
 */
export function getAiBugfixLessonSignalTokens(value: unknown): string[] {
  return tokenizeAiBugfixLessonText(value).filter(
    token => token.length >= 4 && !GENERIC_TOKENS.has(token)
  );
}

/**
 * Classify text into symptom tags for lesson relevance gating.
 *
 * Returns a Set that may contain any of:
 *   `color-visibility`, `position`, `layout`, `typography`
 */
export function classifyAiBugfixLessonSymptoms(value: unknown): Set<string> {
  const text = String(value || '').toLowerCase();
  const symptoms = new Set<string>();

  if (/\b(color|colour|contrast|white|black|readable|visible|visibility|invisible|missing)\b/.test(text)) {
    symptoms.add('color-visibility');
  }
  if (/\b(position|aligned|left|right|center|centred|centered|top|bottom)\b/.test(text)) {
    symptoms.add('position');
  }
  if (/\b(spacing|gap|margin|padding|wrap|overlap|layout|width|height)\b/.test(text)) {
    symptoms.add('layout');
  }
  if (/\b(font|typography|label|text|size|line-height)\b/.test(text)) {
    symptoms.add('typography');
  }

  return symptoms;
}

/** Input shape for lesson scoring. */
export interface LessonScoreInput {
  app: string;
  bugContext: string;
  expectedOutcome: string;
  path: string;
  targetElementHint?: AiLessonTargetElementHint | null;
  /** Optional weight overrides. Merged with LESSON_SCORING_WEIGHTS. */
  weights?: Partial<LessonScoringWeights>;
}

/**
 * Score a single lesson record against the current bug input.
 *
 * Scoring dimensions (additive):
 *   - Same app: +4
 *   - Exact path match: +6; partial path inclusion: +3
 *   - Component chain token overlap: +3 per overlapping token
 *   - Exact `memoryKey.control` match in input text: +8
 *   - Multiselect bonus (gated on color-visibility symptom): +5
 *   - Icon-position bonus (gated on position symptom): +6
 *   - Token overlap: expected-outcome tokens +4 each, others +2 (≥6 chars) or +1 (<6 chars)
 *
 * Gates (return 0 if not passed):
 *   - Symptom compatibility: lesson symptoms must overlap with input symptoms
 *   - Control compatibility: if lesson has a `memoryKey.control`, its tokens must overlap with input tokens
 */
export function scoreAiBugfixLessonRecord(
  record: AiBugfixLessonRecord,
  input: LessonScoreInput
): number {
  const w = { ...LESSON_SCORING_WEIGHTS, ...(input.weights || {}) };
  const targetTokens = [
    input.targetElementHint?.selector,
    ...(input.targetElementHint?.selectorCandidates || []),
    ...(input.targetElementHint?.componentChain || []),
    input.targetElementHint?.surface,
    ...(input.targetElementHint?.relatedOwners || []).flatMap(owner => [
      owner.kind,
      owner.selector,
      owner.tagName,
      ...(owner.classes || [])
    ])
  ];

  const inputText = [
    input.app,
    input.bugContext,
    input.expectedOutcome,
    input.path,
    ...targetTokens
  ].join(' ').toLowerCase();

  const lessonText = JSON.stringify({
    application: record.application,
    memoryKey: record.memoryKey,
    compactedInput: record.compactedInput,
    routeThoughtToFixProblem: record.routeThoughtToFixProblem,
    touchedFiles: record.touchedFiles
  }).toLowerCase();

  const materialInputTokens = [
    ...getAiBugfixLessonSignalTokens(input.bugContext),
    ...getAiBugfixLessonSignalTokens(input.expectedOutcome),
    ...targetTokens.flatMap(token => getAiBugfixLessonSignalTokens(token))
  ];
  const uniqueMaterialInputTokens = [...new Set(materialInputTokens)];

  const inputSymptomTags = classifyAiBugfixLessonSymptoms([
    input.bugContext,
    input.expectedOutcome,
    input.targetElementHint?.selector,
    input.targetElementHint?.surface
  ].join(' '));

  const lessonSymptomTags = classifyAiBugfixLessonSymptoms([
    record.memoryKey?.symptom,
    record.compactedInput?.bugContext,
    record.compactedInput?.expectedResult,
    record.routeThoughtToFixProblem?.summary
  ].join(' '));

  const symptomCompatible =
    lessonSymptomTags.size === 0 || inputSymptomTags.size === 0 ||
    [...lessonSymptomTags].some(symptom => inputSymptomTags.has(symptom));

  const memoryControlTokens = getAiBugfixLessonSignalTokens(record.memoryKey?.control);
  const lessonControlTokens = new Set([
    ...memoryControlTokens,
    ...getAiBugfixLessonSignalTokens(record.routeThoughtToFixProblem?.componentKey),
    ...getAiBugfixLessonSignalTokens(record.routeThoughtToFixProblem?.summary),
    ...getAiBugfixLessonSignalTokens(record.graphFingerprint?.componentHint),
    ...(record.graphFingerprint?.selectorClasses || []).map(c => c.toLowerCase())
  ]);

  // Language-agnostic structural signals from the picked element. Used as a
  // fallback when the user has not typed any text yet (or typed in a
  // language the lesson catalogue does not cover). These tokens are derived
  // from selector classes, component chain, and stable attribute selectors —
  // all of which are language-neutral.
  const structuralInputTokens = new Set<string>();
  const hint = input.targetElementHint;
  for (const candidate of [
    hint?.selector,
    ...(hint?.selectorCandidates || []),
    ...(hint?.componentChain || [])
  ]) {
    if (!candidate) continue;
    for (const cls of String(candidate).matchAll(/\.([A-Za-z0-9-_]+)/g)) {
      structuralInputTokens.add(cls[1].toLowerCase());
    }
  }
  for (const component of hint?.componentChain || []) {
    for (const part of String(component).split('-')) {
      if (part.length >= 4) structuralInputTokens.add(part.toLowerCase());
    }
  }

  const controlOverlap =
    uniqueMaterialInputTokens.some(token => lessonControlTokens.has(token)) ||
    [...structuralInputTokens].some(token => lessonControlTokens.has(token));
  const requiresControlCompatibility = memoryControlTokens.length > 0;

  if (!symptomCompatible || (requiresControlCompatibility && !controlOverlap)) {
    return 0;
  }

  let score = record.application === input.app ? w.sameApp : 0;

  const lessonPath = String(record.compactedInput?.path || '').split('?')[0];
  const inputPath = String(input.path || '').split('?')[0];
  if (lessonPath && lessonPath === inputPath) {
    score += w.exactPath;
  } else if (lessonPath && inputPath && (lessonPath.includes(inputPath) || inputPath.includes(lessonPath))) {
    score += w.partialPath;
  }

  // Component chain similarity
  if (input.targetElementHint?.componentChain?.length) {
    const chainTokens = new Set(
      input.targetElementHint.componentChain.flatMap(c => getAiBugfixLessonSignalTokens(c))
    );
    let chainOverlap = 0;
    for (const token of chainTokens) {
      if (lessonText.includes(token)) chainOverlap++;
    }
    score += chainOverlap * w.chainOverlap;
  }

  const memoryControl = String(record.memoryKey?.control || '').toLowerCase();
  const memorySymptom = String(record.memoryKey?.symptom || '').toLowerCase();

  if (memoryControl && inputText.includes(memoryControl)) {
    score += w.controlExact;
  }

  // Special-case bonuses gated on symptom overlap with current bug
  if (
    memoryControl === 'multiselect' &&
    /p-multiselect|multiselect|streaming/.test(inputText) &&
    inputSymptomTags.has('color-visibility')
  ) {
    score += w.multiselectBonus;
  }
  if (
    memorySymptom === 'icon-position' &&
    /icon|position|aligned|left|right/.test(inputText) &&
    inputSymptomTags.has('position')
  ) {
    score += w.iconPositionBonus;
  }

  const expectedOutcomeTokens = new Set(getAiBugfixLessonSignalTokens(input.expectedOutcome));
  uniqueMaterialInputTokens.forEach(token => {
    if (lessonText.includes(token)) {
      const isExpectedToken = expectedOutcomeTokens.has(token);
      score += isExpectedToken ? w.expectedToken : (token.length >= 6 ? w.longToken : w.shortToken);
    }
  });

  // Graph-match boost: +30 cap. Lights up when a precomputed graphFingerprint
  // on the lesson matches the current target element's class or consumed variable.
  if (record.graphFingerprint) {
    const fp = record.graphFingerprint;
    if (
      fp.variableSource &&
      fp.variableSource !== 'app-override-existing-variable' &&
      fp.variableSource !== 'shared-variable-plus-app-override'
    ) {
      // Only variable routes can grant graph match; other routeActions don't carry a variable contract.
    } else if (fp.variable) {
      const fpVariable = fp.variable.toLowerCase();
      const fpTokens = new Set((fp.tokenSet || []).map(t => t.toLowerCase()));
      const fpClasses = new Set((fp.selectorClasses || []).map(c => c.toLowerCase()));
      const fpComponent = (fp.componentHint || '').toLowerCase();

      const targetClasses = new Set<string>();
      const hint = input.targetElementHint;
      for (const candidate of [
        hint?.selector,
        ...(hint?.selectorCandidates || []),
        ...(hint?.componentChain || [])
      ]) {
        if (!candidate) continue;
        for (const cls of String(candidate).matchAll(/\.([A-Za-z0-9-_]+)/g)) {
          targetClasses.add(cls[1].toLowerCase());
        }
      }

      let graphBoost = 0;
      // Component hint in chain or selector
      if (fpComponent) {
        const chainText = (hint?.componentChain || []).join(' ').toLowerCase();
        const selectorText = (hint?.selector || '').toLowerCase();
        if (chainText.includes(fpComponent) || selectorText.includes(fpComponent)) {
          graphBoost += 5;
        }
      }
      // Class overlap
      let classOverlap = 0;
      for (const cls of fpClasses) {
        if (targetClasses.has(cls)) classOverlap++;
      }
      if (classOverlap > 0) graphBoost += 10;
      // Variable-name token present in current input text (rare; conservative)
      if (fpVariable && (inputText.includes(`--${fpVariable}`) || fpTokens.has(fpVariable))) {
        graphBoost += 20;
      }
      score += Math.min(graphBoost, 30);
    }
  }

  return score;
}

/**
 * Find up to 3 relevant lesson records from the cache, scored against the current input.
 */
export function findRelevantAiBugfixLessons(
  lessons: AiBugfixLessonRecord[],
  input: LessonScoreInput
): AiRelevantBugfixLesson[] {
  const w = { ...LESSON_SCORING_WEIGHTS, ...(input.weights || {}) };

  if (lessons.length === 0) {
    return [];
  }

  const maxScore = computeMaxLessonScore(input);

  return lessons
    .map(record => ({
      record,
      score: scoreAiBugfixLessonRecord(record, input)
    }))
    .filter(({ score }) => score >= w.minScore)
    .sort((left, right) => right.score - left.score)
    .slice(0, 3)
    .map(({ record, score }) => {
      const tier = lessonScoreTier(score, maxScore);
      return {
        score,
        source: 'browser-runtime' as const,
        application: record.application,
        commitId: record.commitId,
        memoryKey: record.memoryKey,
        memoryKeyHash: record.memoryKeyHash,
        compactedInput: record.compactedInput,
        routeThoughtToFixProblem: record.routeThoughtToFixProblem
          ? {
              fixType: record.routeThoughtToFixProblem.fixType,
              routeAction: record.routeThoughtToFixProblem.routeAction,
              componentKey: record.routeThoughtToFixProblem.componentKey,
              summary: record.routeThoughtToFixProblem.summary,
              diagnosticEvidence: record.routeThoughtToFixProblem.diagnosticEvidence,
              reasoning: (record.routeThoughtToFixProblem.reasoning || []).slice(0, 4),
              filesToEdit: (record.routeThoughtToFixProblem.filesToEdit || []).slice(0, 6)
            }
          : undefined,
        touchedFiles: (record.touchedFiles || []).slice(0, 6),
        artifactDir: record.artifactDir,
        scorePercentage: tier.percentage,
        scoreTierColor: tier.color,
        scoreTierLabel: tier.label
      };
    });
}

/**
 * Debug helper: score all lessons and return a detailed breakdown.
 *
 * Produces a table-like breakdown showing each lesson's score components
 * (app, path, control, chain overlap, token overlap, bonuses, gates)
 * so developers can understand why specific lessons matched or didn't.
 *
 * @returns An array of debug records sorted by score descending.
 */
export interface LessonDebugEntry {
  index: number;
  application: string;
  memoryKey: string;
  symptom: string;
  score: number;
  gated: boolean;
  gateReason: string;
  appScore: number;
  pathScore: number;
  chainScore: number;
  controlScore: number;
  bonusScore: number;
  tokenOverlapScore: number;
  tokenMatches: string[];
  threshold: number;
}

export function debugLessonScoring(
  lessons: AiBugfixLessonRecord[],
  input: LessonScoreInput
): LessonDebugEntry[] {
  const w = { ...LESSON_SCORING_WEIGHTS, ...(input.weights || {}) };

  if (lessons.length === 0) {
    return [];
  }

  const results: LessonDebugEntry[] = [];

  for (let i = 0; i < lessons.length; i++) {
    const record = lessons[i];
    const score = scoreAiBugfixLessonRecord(record, input);

    // Recompute breakdown for display (simplified — uses same internals)
    const appMatch = record.application === input.app;
    const lessonPath = String(record.compactedInput?.path || '').split('?')[0];
    const inputPath = String(input.path || '').split('?')[0];
    const exactPathMatch = lessonPath && lessonPath === inputPath;
    const partialPathMatch = !exactPathMatch && lessonPath && inputPath &&
      (lessonPath.includes(inputPath) || inputPath.includes(lessonPath));

    const memoryControl = String(record.memoryKey?.control || '');

    // Symptom gate check
    const inputSymptomTags = classifyAiBugfixLessonSymptoms([
      input.bugContext, input.expectedOutcome,
      input.targetElementHint?.selector, input.targetElementHint?.surface
    ].join(' '));
    const lessonSymptomTags = classifyAiBugfixLessonSymptoms([
      record.memoryKey?.symptom, record.compactedInput?.bugContext,
      record.compactedInput?.expectedResult, record.routeThoughtToFixProblem?.summary
    ].join(' '));
    const symptomCompatible = lessonSymptomTags.size === 0 ||
      [...lessonSymptomTags].some(s => inputSymptomTags.has(s));

    // Control gate check
    const targetTokens = [
      input.targetElementHint?.selector,
      ...(input.targetElementHint?.selectorCandidates || []),
      ...(input.targetElementHint?.componentChain || []),
      input.targetElementHint?.surface,
      ...(input.targetElementHint?.relatedOwners || []).flatMap(owner => [
        owner.kind, owner.selector, owner.tagName, ...(owner.classes || [])
      ])
    ];
    const materialInputTokens = [
      ...getAiBugfixLessonSignalTokens(input.bugContext),
      ...getAiBugfixLessonSignalTokens(input.expectedOutcome),
      ...targetTokens.flatMap(t => getAiBugfixLessonSignalTokens(t))
    ];
    const uniqueTokens = [...new Set(materialInputTokens)];
    const memoryControlTokens = getAiBugfixLessonSignalTokens(record.memoryKey?.control);
    const requiresControl = memoryControlTokens.length > 0;
    const controlPass = !requiresControl || uniqueTokens.some(t => {
      const lessonCtrl = new Set([
        ...memoryControlTokens,
        ...getAiBugfixLessonSignalTokens(record.routeThoughtToFixProblem?.componentKey),
        ...getAiBugfixLessonSignalTokens(record.routeThoughtToFixProblem?.summary)
      ]);
      return lessonCtrl.has(t);
    });

    let gateReason = '';
    if (!symptomCompatible) gateReason = `symptom mismatch (input: [${[...inputSymptomTags].join(',')}], lesson: [${[...lessonSymptomTags].join(',')}])`;
    else if (requiresControl && !controlPass) gateReason = `control mismatch ("${memoryControl}" not in input tokens)`;
    const gated = !symptomCompatible || (requiresControl && !controlPass);

    const appScore = appMatch ? w.sameApp : 0;
    const pathScore = exactPathMatch ? w.exactPath : partialPathMatch ? w.partialPath : 0;

    // Chain overlap
    let chainOverlap = 0;
    if (input.targetElementHint?.componentChain?.length) {
      const lessonText = JSON.stringify({
        application: record.application, memoryKey: record.memoryKey,
        compactedInput: record.compactedInput,
        routeThoughtToFixProblem: record.routeThoughtToFixProblem,
        touchedFiles: record.touchedFiles
      }).toLowerCase();
      const chainTokens = new Set(
        input.targetElementHint.componentChain.flatMap(c => getAiBugfixLessonSignalTokens(c))
      );
      for (const token of chainTokens) {
        if (lessonText.includes(token)) chainOverlap++;
      }
    }

    // Token matches
    const tokenMatches: string[] = [];
    const expectedOutcomeTokens = new Set(getAiBugfixLessonSignalTokens(input.expectedOutcome));
    for (const token of uniqueTokens) {
      const lessonStr = JSON.stringify({
        application: record.application, memoryKey: record.memoryKey,
        compactedInput: record.compactedInput,
        routeThoughtToFixProblem: record.routeThoughtToFixProblem,
        touchedFiles: record.touchedFiles
      }).toLowerCase();
      if (lessonStr.includes(token)) {
        tokenMatches.push(token + (expectedOutcomeTokens.has(token) ? ' [+expected]' : ''));
      }
    }

    // Control score
    const inputText = [
      input.app, input.bugContext, input.expectedOutcome, input.path, ...targetTokens
    ].join(' ').toLowerCase();
    const controlScore = memoryControl && inputText.includes(memoryControl) ? w.controlExact : 0;

    // Bonus
    let bonusScore = 0;
    if (memoryControl === 'multiselect' && /p-multiselect|multiselect|streaming/.test(inputText) && inputSymptomTags.has('color-visibility')) {
      bonusScore += w.multiselectBonus;
    }
    const memorySymptom = String(record.memoryKey?.symptom || '');
    if (memorySymptom === 'icon-position' && /icon|position|aligned|left|right/.test(inputText) && inputSymptomTags.has('position')) {
      bonusScore += w.iconPositionBonus;
    }

    results.push({
      index: i,
      application: record.application || '',
      memoryKey: `${record.memoryKey?.control || ''}/${record.memoryKey?.symptom || ''}`,
      symptom: [...lessonSymptomTags].join(','),
      score,
      gated,
      gateReason,
      appScore,
      pathScore,
      chainScore: chainOverlap * w.chainOverlap,
      controlScore,
      bonusScore,
      tokenOverlapScore: score - appScore - pathScore - (chainOverlap * w.chainOverlap) - controlScore - bonusScore,
      tokenMatches,
      threshold: w.minScore
    });
  }

  return results.sort((a, b) => b.score - a.score);
}

/**
 * Parse a URL query param string of the form `key1:value1,key2:value2`
 * into a Partial<LessonScoringWeights>.
 *
 * Used by the browser chat widget to let developers tweak weights without code changes.
 *
 * @example
 * parseWeightsQueryParam('multiselectBonus:0,minScore:20')
 * // → { multiselectBonus: 0, minScore: 20 }
 */
export function parseWeightsQueryParam(raw: string): Partial<LessonScoringWeights> {
  const weights: Record<string, number> = {};
  const validKeys = new Set(Object.keys(LESSON_SCORING_WEIGHTS));

  for (const part of raw.split(',')) {
    const [key, val] = part.split(':');
    if (key && val && validKeys.has(key.trim())) {
      const num = Number(val.trim());
      if (!isNaN(num)) weights[key.trim()] = num;
    }
  }

  return weights as Partial<LessonScoringWeights>;
}

/** Colour hex values for the 5 score tiers — dark orange → green. */
const SCORE_TIER_COLORS = ['#d35400', '#e67e22', '#f1c40f', '#2ecc71', '#27ae60'] as const;

/** Human-readable labels for each tier. */
const SCORE_TIER_LABELS = ['Weak', 'Fair', 'Moderate', 'Good', 'Strong'] as const;

/**
 * Compute the theoretical maximum score a lesson could achieve for a given input.
 * Used to normalise lesson scores into percentages for the tier indicator.
 */
export function computeMaxLessonScore(input: LessonScoreInput): number {
  const w = { ...LESSON_SCORING_WEIGHTS, ...(input.weights || {}) };

  const targetTokens = [
    input.targetElementHint?.selector,
    ...(input.targetElementHint?.selectorCandidates || []),
    ...(input.targetElementHint?.componentChain || []),
    input.targetElementHint?.surface,
    ...(input.targetElementHint?.relatedOwners || []).flatMap(owner => [
      owner.kind, owner.selector, owner.tagName, ...(owner.classes || [])
    ])
  ];
  const materialInputTokens = [
    ...getAiBugfixLessonSignalTokens(input.bugContext),
    ...getAiBugfixLessonSignalTokens(input.expectedOutcome),
    ...targetTokens.flatMap(t => getAiBugfixLessonSignalTokens(t))
  ];
  const uniqueTokenCount = new Set(materialInputTokens).size;
  const chainTokenCount = input.targetElementHint?.componentChain?.length
    ? new Set(input.targetElementHint.componentChain.flatMap(c => getAiBugfixLessonSignalTokens(c))).size
    : 0;

  return (
    w.sameApp +
    w.exactPath +
    w.controlExact +
    w.multiselectBonus +
    w.iconPositionBonus +
    chainTokenCount * w.chainOverlap +
    uniqueTokenCount * w.expectedToken
  );
}

export interface LessonScoreTier {
  percentage: number;
  color: string;
  label: string;
}

/**
 * Map a lesson score to a tier (0–4) with colour and label.
 *
 * Tier thresholds:
 *   > 85% → green  (Strong)
 *   60–85% → light green (Good)
 *   40–60% → yellow (Moderate)
 *   20–40% → orange (Fair)
 *   < 20% → dark orange (Weak)
 */
export function lessonScoreTier(score: number, maxScore: number): LessonScoreTier {
  const pct = maxScore > 0 ? Math.min(100, Math.round((score / maxScore) * 100)) : 0;

  let tier = 0;
  if (pct > 85) tier = 4;
  else if (pct > 60) tier = 3;
  else if (pct > 40) tier = 2;
  else if (pct > 20) tier = 1;
  // else tier = 0

  return {
    percentage: pct,
    color: SCORE_TIER_COLORS[tier],
    label: SCORE_TIER_LABELS[tier]
  };
}
